import type { IGame } from '../../engine/interfaces/IGame';
import { AudioManager } from '../../engine/audio/AudioManager';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  life: number;
}

interface Target {
  x: number;
  y: number;
  radius: number;
  color: string;
  speed: number;
}

export class GenericArcadeEngine implements IGame {
  readonly name: string;
  onScoreSubmit?: (score: number, sessionMs?: number) => void;
  onLiveScoreUpdate?: (score: number) => void;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private width = 600;
  private height = 400;

  private player = { x: 300, y: 350, width: 24, height: 24, speed: 6 };
  private targets: Target[] = [];
  private particles: Particle[] = [];

  private score = 0;
  private lives = 3;
  private isRunning = false;
  private isPaused = false;
  private startTime = 0;
  private animationFrameId: number | null = null;
  private keys = { left: false, right: false, up: false, down: false, space: false };

  constructor(gameId: string, gameTitle: string) {
    this.name = gameTitle;
  }

  async init(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D context unavailable');
    this.ctx = context;

    this.width = 800;
    this.height = 450;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.canvas.setAttribute('width', (this.width * dpr).toString());
    this.canvas.setAttribute('height', (this.height * dpr).toString());
    this.ctx.scale(dpr, dpr);

    this.resetState();

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  start(): void {
    this.resetState();
    this.isRunning = true;
    this.isPaused = false;
    this.startTime = Date.now();
    this.loop();
  }

  pause(): void { this.isPaused = true; }
  resume(): void { if (this.isPaused) { this.isPaused = false; } }

  destroy(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  }

  private resetState(): void {
    this.score = 0;
    this.lives = 3;
    this.particles = [];
    this.targets = [];
    this.player.x = (this.width - this.player.width) / 2;
    this.player.y = this.height - 40;

    this.spawnTargets();
  }

  private spawnTargets(): void {
    const colors = ['#38bdf8', '#a855f7', '#22c55e', '#eab308', '#ef4444'];
    for (let i = 0; i < 6; i++) {
      this.targets.push({
        x: Math.random() * (this.width - 40) + 20,
        y: Math.random() * -200,
        radius: 14 + Math.random() * 10,
        color: colors[i % colors.length],
        speed: 2 + Math.random() * 2,
      });
    }
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ', 'a', 'd', 'w', 's'].includes(e.key)) {
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' || e.key === 'a') this.keys.left = true;
    if (e.key === 'ArrowRight' || e.key === 'd') this.keys.right = true;
    if (e.key === 'ArrowUp' || e.key === 'w') this.keys.up = true;
    if (e.key === 'ArrowDown' || e.key === 's') this.keys.down = true;
    if (e.key === ' ') this.shootParticle();
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft' || e.key === 'a') this.keys.left = false;
    if (e.key === 'ArrowRight' || e.key === 'd') this.keys.right = false;
    if (e.key === 'ArrowUp' || e.key === 'w') this.keys.up = false;
    if (e.key === 'ArrowDown' || e.key === 's') this.keys.down = false;
    if (e.key === ' ') this.keys.space = false;
  };

  private shootParticle(): void {
    AudioManager.playBeep(700, 0.04, 'square');
    this.particles.push({
      x: this.player.x + this.player.width / 2,
      y: this.player.y,
      vx: (Math.random() - 0.5) * 2,
      vy: -8,
      color: '#38bdf8',
      size: 4,
      life: 60,
    });
  }

  private update(): void {
    if (this.keys.left) this.player.x = Math.max(0, this.player.x - this.player.speed);
    if (this.keys.right) this.player.x = Math.min(this.width - this.player.width, this.player.x + this.player.speed);
    if (this.keys.up) this.player.y = Math.max(0, this.player.y - this.player.speed);
    if (this.keys.down) this.player.y = Math.min(this.height - this.player.height, this.player.y + this.player.speed);

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.life--;

      if (p.life <= 0 || p.y < 0) {
        this.particles.splice(i, 1);
        continue;
      }

      // Check collision with targets
      for (let j = this.targets.length - 1; j >= 0; j--) {
        const t = this.targets[j];
        const dist = Math.hypot(p.x - t.x, p.y - t.y);
        if (dist < t.radius) {
          this.score += 100;
          if (this.onLiveScoreUpdate) this.onLiveScoreUpdate(this.score);
          AudioManager.playBrickBreakSound();
          this.particles.splice(i, 1);
          this.targets.splice(j, 1);

          // Respawn target
          this.targets.push({
            x: Math.random() * (this.width - 40) + 20,
            y: Math.random() * -100,
            radius: 14 + Math.random() * 10,
            color: t.color,
            speed: 2 + Math.random() * 2,
          });
          break;
        }
      }
    }

    // Update targets
    this.targets.forEach((t) => {
      t.y += t.speed;
      if (t.y > this.height) {
        t.y = -20;
        t.x = Math.random() * (this.width - 40) + 20;
      }

      // Check collision with player
      const dist = Math.hypot(this.player.x + this.player.width / 2 - t.x, this.player.y + this.player.height / 2 - t.y);
      if (dist < t.radius + 10) {
        this.lives--;
        AudioManager.playGameOverSound();
        t.y = -50;
        if (this.lives <= 0) {
          this.gameOver();
        }
      }
    });
  }

  private render(): void {
    this.ctx.fillStyle = '#0b0f19';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Player
    this.ctx.fillStyle = '#22c55e';
    this.ctx.shadowColor = '#22c55e';
    this.ctx.shadowBlur = 10;
    this.ctx.fillRect(this.player.x, this.player.y, this.player.width, this.player.height);
    this.ctx.shadowBlur = 0;

    // Targets
    this.targets.forEach((t) => {
      this.ctx.fillStyle = t.color;
      this.ctx.shadowColor = t.color;
      this.ctx.shadowBlur = 8;
      this.ctx.beginPath();
      this.ctx.arc(t.x, t.y, t.radius, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.shadowBlur = 0;
    });

    // Particles
    this.ctx.fillStyle = '#38bdf8';
    this.particles.forEach((p) => {
      this.ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    });

    // UI
    this.ctx.fillStyle = '#f8fafc';
    this.ctx.font = 'bold 15px sans-serif';
    this.ctx.fillText(`${this.name} | SKOR: ${this.score}`, 16, 25);
    this.ctx.fillText(`CAN: ${'🚀 '.repeat(this.lives)}`, this.width - 120, 25);
  }

  private loop = (): void => {
    if (!this.isRunning) return;
    if (!this.isPaused) {
      this.update();
    }
    this.render();
    this.animationFrameId = requestAnimationFrame(this.loop);
  };

  private gameOver(): void {
    this.destroy();
    const sessionMs = Date.now() - this.startTime;
    if (this.onScoreSubmit) {
      this.onScoreSubmit(this.score, sessionMs);
    }
  }
}
